import { useStyles } from "./DowntimeReport.styles";
import { IDowntimeReport } from "./DowntimeReportInterfaces";
import EastIcon from "@mui/icons-material/East";
import DowntimeItem from "./DowntimeItem";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { IconButton } from "@mui/material";
import useSteps from "hooks/useSteps";
import { useTranslation } from "react-i18next";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { LiveDowntimeReportEntity } from "entities/LiveDowntimeReportEntity/LiveDowntimeReportEntity";
import CardDetailReport from "./Components/CardDetailReport";
import { OverallSummary } from "pages/HMI/HMIDashboard/OverallSummary/OverallSummary.view";
import { END_OF_SHIFT_FAILURE_CODE_NAME, MEASURE_METRICS, UPDATEMINUTESRATEFORHMIDATA } from "constants/constants";
import { TopFiveFunctionalLocations } from "pages/HMI/HMIDashboard/TopFiveFunctionalLocations/TopFiveFunctionalLocations.view";
import { TopFiveFailureCodes } from "pages/HMI/TopFiveFailureCodes/TopFiveFailureCodes.view";
import {
    IEventWithHours,
    ILastTwoShiftsEvents,
} from "entities/LiveDowntimeReportEntity/LiveDowntimeReportEntityInterfaces";
import MainAccordion from "components/MainAccordion/MainAccordion.view";
import { IDowntimeData } from "../LiveDowntimeReportInterfaces";
import { HMIContext } from "contexts/HMIContext/HMIContext";
import { ShiftOptionsEnum } from "components/ShiftDropdown/ShiftDropdown.view";
import { useParams } from "react-router-dom";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { MillDetailEntity } from "entities/MillDetail/MillDetailEntity";
import moment from "moment";
import { LocationsContext } from "contexts/LocationsContext/LocationsContext";

enum GraphPagesEnum {
    overallDowntimeSummary,
    topFiveFunctionalLocations,
    topFiveFailureCodes,
}

const DowntimeReport = ({ liveDowntimeReport }: IDowntimeReport) => {
    const classes = useStyles();
    const cardRef = useRef<HTMLDivElement>(null);
    const itemsRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [cardRect, setCardRect] = useState<DOMRect | undefined>();
    const [itemRect, setItemRect] = useState<DOMRect | undefined>();
    const [selectedDowntime, setSelectedDowntime] = useState<number | null>(null);
    const [selectedMachineCenter, setSelectedMachineCenter] = useState<string | undefined>(undefined);
    const [selectedDowntimeIndex, setSelectedDowntimeIndex] = useState<number | null>(null);
    const [downtimeEvents, setDowntimeEvents] = useState<any>([]);
    const { shift } = useContext(HMIContext);
    const { millTimezone } = useContext(LocationsContext);

    const { millId } = useParams();

    const { millCountryCode } = MillsEntity.GetMillCountryCode(Number(millId));
    const { countryCode: corporateCountryCode } = MillDetailEntity.GetCorporateZipcodeAndCountry(Number(millId));
    const [countryCode, setCountryCode] = useState<string>("");

    useEffect(() => {
        if (millCountryCode) {
            setCountryCode(millCountryCode);
        } else if (corporateCountryCode) {
            setCountryCode(corporateCountryCode);
        }
    }, [millCountryCode, corporateCountryCode]);

    useEffect(() => {
        if (selectedDowntimeIndex !== null) {
            const downtimeEventsData = async () => {
                const nowMillTimezone = moment().tz(millTimezone as string);
                const data = (await LiveDowntimeReportEntity.GetLastTwoShiftsEvents(
                    Number(liveDowntimeReport.downtime_data[selectedDowntimeIndex!].machineCenterId),
                    nowMillTimezone,
                )) as unknown as ILastTwoShiftsEvents;

                if (data) {
                    const filteredDowntimeEvents: IEventWithHours[] =
                        shift === ShiftOptionsEnum.current
                            ? data.currentShiftEventWithHours.filter(
                                  (event: IEventWithHours) =>
                                      event.measure_metric_id === MEASURE_METRICS.DOWNTIME &&
                                      event.failure_code?.code !== END_OF_SHIFT_FAILURE_CODE_NAME,
                              )
                            : data.previousShiftEventWithHours.filter(
                                  (event: IEventWithHours) =>
                                      event.measure_metric_id === MEASURE_METRICS.DOWNTIME &&
                                      event.failure_code?.code !== END_OF_SHIFT_FAILURE_CODE_NAME,
                              );

                    setDowntimeEvents(filteredDowntimeEvents);
                }
            };

            downtimeEventsData();

            const interval = setInterval(async () => {
                await downtimeEventsData();
            }, UPDATEMINUTESRATEFORHMIDATA);

            return () => clearInterval(interval);
        }
    }, [liveDowntimeReport.downtime_data, selectedDowntimeIndex, shift, millTimezone]);

    const { t } = useTranslation();

    const graphsSteps = useMemo(
        () => [
            {
                id: GraphPagesEnum.overallDowntimeSummary,
                label: `${t("locations.mill_dashboards.live_downtime_report.overall_downtime_summary")} ${
                    selectedMachineCenter && `- ${selectedMachineCenter}`
                }`,
            },
            {
                id: GraphPagesEnum.topFiveFunctionalLocations,
                label: t("locations.mill_dashboards.live_downtime_report.top_5_bad_actors_functional_locations"),
            },
            {
                id: GraphPagesEnum.topFiveFailureCodes,
                label: t("locations.mill_dashboards.live_downtime_report.top_5_failure_codes_by_downtime_type"),
            },
        ],
        [selectedMachineCenter, t],
    );

    const { activeStep, handleNextStep, handlePreviousStep, setStepManually } = useSteps({
        steps: graphsSteps,
        initialStep: GraphPagesEnum.overallDowntimeSummary,
        infinity: true,
    });

    const handleDowntimeItemToggle = (downtimeId: number, downtimeIndex: number, machineCenterName: string) => {
        if (selectedDowntime === downtimeId) {
            setSelectedDowntime(null);
            setSelectedDowntimeIndex(null);
            setSelectedMachineCenter(undefined);
        } else {
            setSelectedDowntime(downtimeId);
            setSelectedDowntimeIndex(downtimeIndex);
            setSelectedMachineCenter(machineCenterName);
        }
    };

    const calculateCardAndItemRects = useCallback(() => {
        if (selectedDowntimeIndex !== null && itemsRefs.current[selectedDowntimeIndex]) {
            setCardRect(cardRef.current?.getBoundingClientRect());
            setItemRect(itemsRefs.current[selectedDowntimeIndex]?.getBoundingClientRect());
        }
    }, [selectedDowntimeIndex]);

    const leftArrowValue = useMemo(() => {
        if (selectedDowntimeIndex !== null) {
            const cardRectValue = cardRect || cardRef.current?.getBoundingClientRect();
            const itemRectValue = itemRect || itemsRefs.current[selectedDowntimeIndex]?.getBoundingClientRect();
            if (cardRectValue && itemRectValue) {
                return itemRectValue.left - cardRectValue.left + itemRectValue.width / 4;
            }
        }
        return 0;
    }, [cardRect, itemRect, selectedDowntimeIndex]);

    useEffect(() => {
        if (selectedDowntimeIndex !== null && itemsRefs.current[selectedDowntimeIndex]) {
            setCardRect(cardRef.current?.getBoundingClientRect());
            setItemRect(itemsRefs.current[selectedDowntimeIndex]?.getBoundingClientRect());
        }
    }, [selectedDowntimeIndex]);

    useEffect(() => {
        const listener = () => {
            calculateCardAndItemRects();
        };
        window.addEventListener("resize", listener);

        return () => {
            window.removeEventListener("resize", listener);
        };
    }, [calculateCardAndItemRects]);

    const handleOverallDowntimeSummaryClose = () => {
        setSelectedDowntime(null);
        setSelectedDowntimeIndex(null);
    };

    const graphPages = {
        [GraphPagesEnum.overallDowntimeSummary]: {
            component: (
                <OverallSummary
                    downtimeEvents={downtimeEvents}
                    downtimeSelected={liveDowntimeReport.downtime_data[selectedDowntimeIndex!]}
                    showDowntimeDetailsHeader={true}
                    countryCode={countryCode}
                />
            ),
        },
        [GraphPagesEnum.topFiveFunctionalLocations]: {
            component: <TopFiveFunctionalLocations downtimeEvents={downtimeEvents} countryCode={countryCode} />,
        },
        [GraphPagesEnum.topFiveFailureCodes]: {
            component: (
                <TopFiveFailureCodes
                    downtimeEvents={downtimeEvents}
                    liveDowntimeReport={true}
                    countryCode={countryCode}
                />
            ),
        },
    };

    return (
        <MainAccordion cardRef={cardRef} title={liveDowntimeReport.name}>
            <>
                {liveDowntimeReport.downtime_data.map((downtime: IDowntimeData, index: number) => {
                    if (index !== liveDowntimeReport.downtime_data.length - 1) {
                        return (
                            <div className={classes.itemSeparator} key={downtime.id}>
                                <DowntimeItem
                                    ref={(ref) => {
                                        itemsRefs.current[index] = ref;
                                    }}
                                    downtimeData={downtime}
                                    isSelected={selectedDowntime === downtime.id}
                                    onClick={() =>
                                        handleDowntimeItemToggle(
                                            downtime.id,
                                            index,
                                            downtime.shortName || downtime.name,
                                        )
                                    }
                                />
                                <EastIcon />
                            </div>
                        );
                    }
                    return (
                        <DowntimeItem
                            ref={(ref) => {
                                itemsRefs.current[index] = ref;
                            }}
                            key={downtime.id}
                            downtimeData={downtime}
                            isSelected={selectedDowntime === downtime.id}
                            onClick={() =>
                                handleDowntimeItemToggle(downtime.id, index, downtime.shortName || downtime.name)
                            }
                        />
                    );
                })}
                {selectedDowntime && (
                    <div className={classes.downtimeSummaryWrapper}>
                        <div className={classes.arrow} style={{ left: leftArrowValue }} />
                        <div className={classes.downtimeSummaryBody}>
                            <CardDetailReport title={activeStep.label} onClose={handleOverallDowntimeSummaryClose}>
                                {
                                    // @ts-ignore
                                    graphPages[activeStep.id].component
                                }
                            </CardDetailReport>
                        </div>
                        <IconButton className={classes.arrowLeftButton} onClick={handlePreviousStep}>
                            <ChevronLeftIcon />
                        </IconButton>
                        <IconButton className={classes.arrowRightButton} onClick={handleNextStep}>
                            <ChevronRightIcon />
                        </IconButton>
                        <div className={classes.dotsWrapper}>
                            {graphsSteps.map((step) => {
                                return (
                                    <div
                                        className={`${classes.dots} ${
                                            activeStep.id === step.id ? classes.dotActive : classes.dotInactive
                                        }`}
                                        onClick={() => setStepManually(step.id)}
                                        key={step.id}
                                    />
                                );
                            })}
                        </div>
                    </div>
                )}
            </>
        </MainAccordion>
    );
};

export default DowntimeReport;
